/**
 * El dinero (CLAUDE.md §3, §23). **Todo importe se guarda y se suma en la unidad
 * más pequeña de la moneda: céntimos, enteros.**
 *
 * `numeric` de Postgres es exacto, pero JavaScript lee un `numeric` como un
 * `number`, que es un flotante: `19.99 * 3` da `59.97000000000001`. Un céntimo de
 * diferencia en un pedido es una llamada del cliente, y a la décima es un
 * descuadre contable. Con enteros no hay nada que redondear en mitad de una suma;
 * el único redondeo está aquí, al entrar, y se hace una sola vez.
 *
 * «Los redondeos en una tienda no se negocian» (§3).
 */

/** Monedas admitidas en la primera versión. Multimoneda no entra (§23): es una por cliente. */
export const MONEDAS = ['PEN', 'USD', 'EUR', 'MXN', 'COP', 'CLP', 'ARS'] as const
export type Moneda = (typeof MONEDAS)[number]

/** Las que no usan céntimos: un importe es el número entero de unidades. */
const SIN_DECIMALES = new Set<Moneda>(['CLP', 'COP'])

export const decimalesDe = (moneda: Moneda): number => (SIN_DECIMALES.has(moneda) ? 0 : 2)

export const esMoneda = (valor: unknown): valor is Moneda => MONEDAS.includes(valor as Moneda)

/**
 * De lo que se escribe en el panel (19.99) a lo que se guarda (1999). Se redondea
 * al céntimo más cercano **una sola vez**, aquí; a partir de este punto ya no hay
 * decimales que perder.
 */
export const aCentimos = (importe: number, moneda: Moneda = 'PEN'): number => {
  if (!Number.isFinite(importe)) throw new Error('Un importe tiene que ser un número.')
  const factor = 10 ** decimalesDe(moneda)
  // El .5 se redondea hacia arriba en valor absoluto, que es lo que espera cualquiera
  // que mire un precio; `Math.round(-0.5)` daría 0 y rompería una devolución.
  return Math.sign(importe) * Math.round(Math.abs(importe) * factor)
}

export const deCentimos = (centimos: number, moneda: Moneda = 'PEN'): number => centimos / 10 ** decimalesDe(moneda)

const SIMBOLOS: Record<Moneda, string> = { PEN: 'S/', USD: '$', EUR: '€', MXN: '$', COP: '$', CLP: '$', ARS: '$' }

/**
 * La convención de cada moneda: cómo la escribe la gente del país que la usa. Con un `es` a
 * secas, `Intl` no conoce el símbolo del sol ni del peso y escribe «19,99 PEN»; con el país
 * escribe «S/ 19.99», «$420.000» o «19,99 €», que es lo que espera quien compra.
 */
const CONVENCIONES: Record<Moneda, string> = { PEN: 'es-PE', USD: 'es-US', EUR: 'es-ES', MXN: 'es-MX', COP: 'es-CO', CLP: 'es-CL', ARS: 'es-AR' }

/**
 * Cómo se enseña. `Intl` conoce las reglas de cada sitio —dónde va el símbolo,
 * qué separa los miles—, así que no se inventan aquí: sin idioma, va la convención
 * del país de la moneda. Si el entorno no trae los datos de esa moneda, se cae a
 * algo legible en vez de reventar.
 */
export const formatear = (centimos: number, moneda: Moneda = 'PEN', idioma?: string): string => {
  const decimales = decimalesDe(moneda)
  try {
    return new Intl.NumberFormat(idioma ?? CONVENCIONES[moneda] ?? 'es', { style: 'currency', currency: moneda, minimumFractionDigits: decimales, maximumFractionDigits: decimales }).format(deCentimos(centimos, moneda))
  } catch {
    return `${SIMBOLOS[moneda] ?? ''}${deCentimos(centimos, moneda).toFixed(decimales)}`
  }
}

/** Un porcentaje de un importe, en céntimos y sin decimales sueltos. */
export const porcentaje = (centimos: number, tanto: number): number => Math.round((centimos * tanto) / 100)

/**
 * Un precio escrito a mano: no negativo, y con los decimales que la moneda admite.
 * Devuelve el motivo por el que no vale, o `null`.
 */
export const motivoDePrecioInvalido = (importe: unknown, moneda: Moneda = 'PEN'): null | string => {
  if (typeof importe !== 'number' || !Number.isFinite(importe)) return 'Escribe el precio como un número.'
  if (importe < 0) return 'Un precio no puede ser negativo.'
  if (importe > 1_000_000_000) return 'Ese precio es demasiado grande.'
  const decimales = decimalesDe(moneda)
  const factor = 10 ** decimales
  if (Math.abs(importe * factor - Math.round(importe * factor)) > 1e-6) {
    return decimales === 0 ? `En ${moneda} los precios no llevan decimales.` : `Un precio en ${moneda} lleva ${decimales} decimales como mucho.`
  }
  return null
}

/**
 * Un número escrito a mano, con los separadores que use cada uno: «20», «20,50», «20.50»,
 * «1.234,56», «1,234.56», «S/ 15.000». Lo que no es un número, `null`.
 *
 * Cuando hay dos separadores, el último es el decimal. Con uno solo se mira lo que lleva
 * detrás: tres cifras son miles («1.234», «15,000»), una o dos son decimales («39,90»).
 * En una moneda sin decimales (CLP, COP) todo separador es de miles salvo que vaya
 * seguido de una o dos cifras, y entonces el importe no vale (no lleva decimales): mejor
 * decírselo que devolver cien veces más o menos de lo que quería.
 */
export const numeroEscrito = (texto: string): null | number => {
  let t = texto.replace(/[^\d.,-]/g, '')
  if (!/\d/.test(t) || /-/.test(t.slice(1))) return null
  const puntos = (t.match(/\./g) ?? []).length
  const comas = (t.match(/,/g) ?? []).length
  if (puntos && comas) {
    t = t.lastIndexOf(',') > t.lastIndexOf('.') ? t.replace(/\./g, '').replace(',', '.') : t.replace(/,/g, '')
  } else if (comas) {
    t = comas === 1 && !/,\d{3}$/.test(t) ? t.replace(',', '.') : t.replace(/,/g, '')
  } else if (puntos > 1 || /\.\d{3}$/.test(t)) {
    t = t.replace(/\./g, '')
  }
  const n = Number(t)
  return t !== '' && Number.isFinite(n) ? n : null
}

/**
 * Un importe que escribe una persona —una devolución parcial desde el panel—, en céntimos
 * de su moneda: un número, o un texto como se escribe («25,50», «S/ 1.234,56»). Lo
 * convierte `aCentimos`, una sola vez (§3). Devuelve el motivo en llano si no vale.
 */
export const leerImporte = (valor: unknown, moneda: Moneda): { centimos: number } | { motivo: string } => {
  const decimales = decimalesDe(moneda)
  const ejemplo = decimales === 0 ? 'por ejemplo 15000' : 'por ejemplo 25,50'
  const n = typeof valor === 'number' ? (Number.isFinite(valor) ? valor : null) : typeof valor === 'string' ? numeroEscrito(valor) : null
  if (n === null) return { motivo: `Escribe el importe como un número, ${ejemplo}.` }
  if (n <= 0) return { motivo: 'El importe tiene que ser mayor que cero.' }
  if (n > 1_000_000_000) return { motivo: 'Ese importe es demasiado grande.' }
  const factor = 10 ** decimales
  if (Math.abs(n * factor - Math.round(n * factor)) > 1e-6) {
    return { motivo: decimales === 0 ? `En ${moneda} los importes no llevan decimales: ${ejemplo}.` : `Un importe en ${moneda} lleva ${decimales} decimales como mucho.` }
  }
  return { centimos: aCentimos(n, moneda) }
}
